import {
    CUSTOM_FIELD_PREFIX,
    customFieldFormValues,
    groupFieldsByHeaders,
    isMandatoryFor,
    useCustomFields,
} from 'components/forms/inputs/custom-fields/useCustomFields';
import { SectionSaveRefused } from 'components/section-edit/SectionSaveRefused';
import useAuth from 'hooks/useAuth';
import { useMemo } from 'react';
import { useGetActiveBankAccountsQuery } from 'store/api/companies';
import { useIsEinvoicingEnabled } from 'utils/useIsEinvoicingEnabled';
import { ClassifierData } from 'types/classifiers';
import { CompanyBankAccountsGroupedDto } from 'types/companies';
import { AddressDto, ContactDto, ContactForm, PartyType, RelationshipTypeCode } from 'types/contacts';
import { CustomField, CustomFieldDto, CustomFieldType } from 'types/customFields';
import { EntityType } from 'types/entity';
import { contactKind, Setting } from 'types/back-office-settings';
import { FeatureFlag } from 'types/features';
import { formatFullAddress } from 'utils/addressFormatter';
import { t } from 'utils/getTranslations';
import { isPersonalCodeMask } from 'utils/personalCodeMask';
import { useClassifiers } from 'utils/useClassifiers';
import { requiredFieldReaders } from 'utils/useRequiredFields';
import { settingReaders } from 'utils/useSettings';
import { contactDtoToForm } from '../contactFormMapping';
import { MergeChoice, MergeChoices } from './types';
import { useAdministratorField } from 'hooks/useAdministratorField';

/** What decides whether the edit form shows a field, beyond the contact's own values. */
export type MergeFieldEnv = {
    /** Whether any of the flags is on. */
    isEnabled: (...flags: FeatureFlag[]) => boolean;
    isSettingEnabled: (key: Setting) => boolean;
    /** Whether the Client form's Required fields card asks the signed-in colleague for the field on this contact. */
    isRequiredFor: (field: string, form: ContactForm) => boolean;
    vatOptionalInOwnCountry: boolean;
    isEinvoicingEnabled: boolean;
    /** The values the field labels interpolate: `name`, what the firm calls the Administrator/Assistant field. */
    labelValues: { name: string };
};

export const mergeFieldEnv = (
    enabledFeatures: string[] | null | undefined,
    enabledSettings: string[] | null | undefined,
    isEinvoicingEnabled: boolean,
    requiredFields: string[] | null | undefined,
    administratorFieldName: string
): MergeFieldEnv => {
    const { isRequired, vatOptionalInOwnCountry } = requiredFieldReaders(requiredFields);
    return {
        isEnabled: (...flags) => flags.some((flag) => enabledFeatures?.includes(flag)),
        ...settingReaders(enabledSettings),
        isRequiredFor: (field, form) => isRequired('CLIENT', field, contactKind(form.partyType, form.relationshipType)),
        vatOptionalInOwnCountry,
        isEinvoicingEnabled,
        labelValues: { name: administratorFieldName },
    };
};

/** A field the Client form's Required fields card asks for. */
const cardRequired = (field: string) => (form: ContactForm, env: MergeFieldEnv) => env.isRequiredFor(field, form);

/** A field of the contact edit form that step 2 compares and the merge combines. */
export type MergeField = {
    /** The `ContactForm` key, or `customField_<id>`. */
    key: string;
    /** The form section's title key, or a custom field's raw section name. */
    section: string;
    /** The form's label key, or a custom field's own label. */
    labelKey: string;
    /** `text` is free text, which combines the texts of the contacts ticked in step 2. */
    kind: 'single' | 'multi' | 'text';
    /** Absent when the form shows the field for both party types. */
    partyType?: PartyType;
    /** The form's own condition for showing the field, for one contact's values. */
    isShown?: (form: ContactForm, env: MergeFieldEnv) => boolean;
    /**
     * The form's own condition for requiring the field, for the merged contact's values. Step 2 reads it on its
     * checkbox rows: every box of a field without it can be unticked, which leaves the field empty. The About card
     * reads it for the fields its relationship type change makes required (`missingRequiredFields`).
     */
    isRequired?: (form: ContactForm, env: MergeFieldEnv) => boolean;
    /** Set on a custom field: its section is raw text, and its label too unless the field is translated. */
    custom?: { translateLabel: boolean };
};

/** The edit form's section title keys, in form order. */
export const FORM_SECTIONS = {
    general: 'contacts.about.title',
    person: 'contacts.person.title',
    company: 'contacts.company.title',
    identityDocument: 'contacts.identity-document.title',
    contactDetails: 'contacts.contact-details',
    billing: 'contacts.billing.title',
    marketing: 'contacts.marketing.title',
    other: 'contacts.other.title',
    compliance: 'contacts.compliance.title',
};

const S = FORM_SECTIONS;

function single(key: keyof ContactForm, section: string, labelKey: string, more?: Partial<MergeField>): MergeField {
    return { key, section, labelKey, kind: 'single', ...more };
}

function multi(key: keyof ContactForm, section: string, labelKey: string, more?: Partial<MergeField>): MergeField {
    return { key, section, labelKey, kind: 'multi', ...more };
}

function text(key: keyof ContactForm, section: string, labelKey: string, more?: Partial<MergeField>): MergeField {
    return { key, section, labelKey, kind: 'text', ...more };
}

function isClient(form: ContactForm) {
    return form.relationshipType === RelationshipTypeCode.CLIENT;
}

function isClientOrLead(form: ContactForm) {
    return isClient(form) || form.relationshipType === RelationshipTypeCode.LEAD;
}

function flagged(...flags: FeatureFlag[]) {
    return (_form: ContactForm, env: MergeFieldEnv) => env.isEnabled(...flags);
}

function settingOn(key: Setting) {
    return (_form: ContactForm, env: MergeFieldEnv) => env.isSettingEnabled(key);
}

const localized = flagged(FeatureFlag.LOCALIZED_FIELDS, FeatureFlag.LOCALIZED_FIELDS_CONTACTS);

const person: Partial<MergeField> = { partyType: PartyType.PERSON };
const company: Partial<MergeField> = { partyType: PartyType.COMPANY };
const identityDocument: Partial<MergeField> = { ...person, isShown: settingOn(Setting.IDENTITY_DOCUMENT_FIELDS) };
const eInvoicing: Partial<MergeField> = { ...company, isShown: (_form, env) => env.isEinvoicingEnabled };
const clientOnly: Partial<MergeField> = { isShown: isClient };

/**
 * Every field `ContactPageForm` renders, in its order, with its section, label and visibility rule. Keep it in
 * step with that form: nothing checks that a field added there is listed here. The compliance fields and the
 * blocking policy are left out on purpose, because the merge preview combines them by rule.
 */
const FORM_FIELDS: MergeField[] = [
    single('relationshipType', S.general, 'contacts.about.relationship-type'),
    multi('partners', S.general, 'contacts.about.relationship-partner', { isRequired: isClientOrLead }),
    multi('managers', S.general, 'contacts.about.relationship-manager', {
        isRequired: cardRequired('RELATIONSHIP_MANAGER'),
    }),
    multi('administrators', S.general, 'contacts.about.client-assistant', {
        isShown: settingOn(Setting.ADMINISTRATOR_FIELD),
        isRequired: cardRequired('ASSISTANT'),
    }),

    single('firstName', S.person, 'first-name', person),
    single('lastName', S.person, 'last-name', person),
    single('name', S.person, 'contacts.display-name-unofficial', person),
    single('localFirstName', S.person, 'first-name-local', { ...person, isShown: localized }),
    single('localLastName', S.person, 'last-name-local', { ...person, isShown: localized }),
    single('personTitle', S.person, 'title', {
        ...person,
        isShown: (_form, env) => !env.isSettingEnabled(Setting.PERSON_TITLES_FREE_TEXT),
    }),
    single('personManualTitle', S.person, 'title', {
        ...person,
        isShown: settingOn(Setting.PERSON_TITLES_FREE_TEXT),
    }),
    single('position', S.person, 'contacts.position', person),
    multi('relatedKeywords', S.person, 'contacts.related-keywords', person),
    multi('areaOfResponsibilityTypes', S.person, 'contacts.areas-of-responsibility', person),
    single('personalCode', S.person, 'contacts.person-id', {
        ...person,
        isRequired: cardRequired('PERSONAL_ID_CODE'),
    }),
    single('linkedinUrl', S.person, 'contacts.linkedin', person),
    single('homeAddress', S.person, 'address', person),
    single('homeLocalAddress', S.person, 'address-local', {
        ...person,
        isShown: (form, env) => isClient(form) && localized(form, env),
    }),
    multi('businessAreas', S.person, 'business-areas', {
        ...person,
        isShown: isClient,
        isRequired: cardRequired('BUSINESS_AREAS'),
    }),
    multi('businessContacts', S.person, 'contacts.business-contact-of', person),
    multi('employedBy', S.person, 'contacts.employed-by', person),
    single('segmentType', S.person, 'contacts.segment-type', person),
    single('serviceLevelType', S.person, 'contacts.service-level-type', person),

    single('officialName', S.company, 'contacts.company.name', company),
    single('name', S.company, 'contacts.unofficial-name', company),
    single('officialLocalName', S.company, 'official-name-local', { ...company, isShown: localized }),
    single('regCode', S.company, 'reg-number', {
        ...company,
        isRequired: cardRequired('REGISTRATION_NUMBER'),
    }),
    single('website', S.company, 'website', company),
    multi('businessAreas', S.company, 'business-areas', { ...company, isRequired: cardRequired('BUSINESS_AREAS') }),
    single('registrationAddress', S.company, 'registration-address', { ...company, isRequired: isClient }),
    single('registrationLocalAddress', S.company, 'registration-address-local', { ...company, isShown: localized }),
    single('segmentType', S.company, 'contacts.segment-type', company),
    single('serviceLevelType', S.company, 'contacts.service-level-type', company),

    single('identityDocumentNumber', S.identityDocument, 'contacts.identity-document.number', identityDocument),
    single(
        'identityDocumentDateOfIssue',
        S.identityDocument,
        'contacts.identity-document.date-of-issue',
        identityDocument
    ),
    single(
        'identityDocumentDateOfExpiry',
        S.identityDocument,
        'contacts.identity-document.date-of-expiry',
        identityDocument
    ),
    single(
        'identityDocumentIssuingCountryCode',
        S.identityDocument,
        'contacts.identity-document.issuing-country',
        identityDocument
    ),
    single(
        'identityDocumentIssuingAuthority',
        S.identityDocument,
        'contacts.identity-document.issuing-authority',
        identityDocument
    ),

    single('mainPhone', S.contactDetails, 'phone'),
    single('mainMobile', S.contactDetails, 'mobile-phone'),
    single('mainEmail', S.contactDetails, 'email', {
        isRequired: cardRequired('EMAIL'),
    }),
    single('correspondenceAddress', S.contactDetails, 'contacts.correspondence-address', {
        isShown: settingOn(Setting.CORRESPONDENCE_ADDRESS),
    }),
    single('visitingAddress', S.contactDetails, 'visiting-address', company),
    multi('employees', S.contactDetails, 'employees.employees', company),
    single('primaryContact', S.contactDetails, 'contacts.primary-contact', {
        ...company,
        isRequired: cardRequired('PRIMARY_CONTACT'),
    }),
    multi('partyGroups', S.contactDetails, 'contacts.contact-groups'),

    single('isInternalClient', S.billing, 'contacts.internal-client'),
    single('clientReference', S.billing, 'contacts.about.client-reference'),
    single('notVatLiable', S.billing, 'not-vat-liable', company),
    single('vatRegNum', S.billing, 'vat-number', {
        ...company,
        isShown: (form) => !form.notVatLiable,
        // With the own-country tick on, whether the card asks for it depends on the firm's own country: the backend
        // decides that one.
        isRequired: (form, env) =>
            !form.notVatLiable &&
            (!!form.vatCountryCode || (cardRequired('VAT_NUMBER')(form, env) && !env.vatOptionalInOwnCountry)),
    }),
    single('vatCountryCode', S.billing, 'vat-country', { ...company, isShown: (form) => !form.notVatLiable }),
    single('preferredBankAccount', S.billing, 'contacts.preferred-bank-account', {
        isShown: (form, env) => isClient(form) && env.isEnabled(FeatureFlag.PARTY_PAYMENT_ACCOUNT_NUMBER),
    }),
    single('invoicingPaymentDays', S.billing, 'financial-conditions.payment-deadline', clientOnly),
    text('billingNotes', S.billing, 'contacts.billing-notes'),
    text('invoicingInfo', S.billing, 'contacts.invoicing-info-internal', {
        isShown: flagged(FeatureFlag.INVOICING_INFO_ENABLED),
    }),
    single('billingAddress', S.billing, 'billing-address', { isRequired: isClient }),
    single('billingLocalAddress', S.billing, 'billing-address-local', { isShown: localized }),
    single('preferredLanguage', S.billing, 'contacts.billing.invoice-lang', { isRequired: isClient }),
    single('einvoiceOperatorType', S.billing, 'einvoiceOperatorType.title', eInvoicing),
    single('einvoiceAddress', S.billing, 'einvoiceAddress.title', eInvoicing),
    single('einvoiceOperatorCode', S.billing, 'einvoiceOperatorCode.title', {
        ...company,
        isShown: (form, env) => env.isEinvoicingEnabled && form.einvoiceOperatorType?.value !== 'PEPPOL',
    }),
    single('payerParty', S.billing, 'contacts.billing.payer-party'),
    single('useJointInvoicing', S.billing, 'contacts.billing.use-joint-invoicing'),
    single('issueInvoicesWithCurrentDate', S.billing, 'contacts.billing.issue-invoices-with-current-date', {
        isShown: flagged(FeatureFlag.INVOICING_DATE_ON_PARTY_FORM),
    }),
    single('primaryRecipientParty', S.billing, 'matters.primary-recipient'),
    single('primaryRecipientAdditionalEmails', S.billing, 'matters.primary-recipient-emails'),
    multi('secondaryRecipientParties', S.billing, 'matters.secondary-recipient'),
    single('secondaryRecipientAdditionalEmails', S.billing, 'matters.secondary-recipient-emails'),
    single('invoicingDeadlineDay', S.billing, 'matters.client-invoicing-deadline-day', clientOnly),
    text('externalBillingInfo', S.billing, 'matters.client-external-billing-info', clientOnly),
    single('invoiceArchiveEmail', S.billing, 'contacts.billing.invoice-archive-email', clientOnly),
    multi('paymentNotificationReceivers', S.billing, 'contacts.billing.invoice-payment-notification-to', clientOnly),

    single('preferredMailingLanguageTypeCode', S.marketing, 'contacts.marketing.preferred-mailing-language', {
        ...person,
        isRequired: cardRequired('MAILING_LANGUAGE'),
    }),
    multi('mailingPreferences', S.marketing, 'contacts.marketing.mailing-preferences', person),
    multi('marketingAreaTypes', S.marketing, 'contacts.marketing.preferred-marketing-areas', person),
    multi('countries', S.marketing, 'contacts.marketing.jurisdictions-of-interest', person),

    single('dmsLink', S.other, 'contacts.dms-link'),
    text('notes', S.other, 'contacts.other.notes'),
    text('coiNotes', S.other, 'contacts.other.coi-notes'),
    multi('relatedKeywords', S.other, 'contacts.related-keywords', company),
    multi('cooperationAreaTypes', S.other, 'contacts.other.confirmed-cooperation-areas'),
];

/** Fields the edit form saves back as loaded without rendering them. Step 2 cannot offer them. */
const UNRENDERED_FIELDS: MergeField[] = [text('debtNotes', S.billing, 'contacts.debt-notes')];

/**
 * The edit form's fields followed by one per custom field of the party type, as the form groups them. Whether a
 * custom field is mandatory comes from its definition: `customFields` has it settled for no relationship type.
 */
export function mergeFieldRegistry(customFields: CustomField[], definitions = customFields): MergeField[] {
    return [
        ...FORM_FIELDS,
        // A copy: groupFieldsByHeaders sorts the array it is given in place.
        ...groupFieldsByHeaders([...customFields]).flatMap(({ section, fields }) =>
            fields.map((field): MergeField => {
                const definition = definitions.find(({ id }) => id === field.id) ?? field;
                return {
                    key: CUSTOM_FIELD_PREFIX + field.id,
                    section,
                    labelKey: field.label ?? '',
                    kind: field.isMultivalue
                        ? 'multi'
                        : field.fieldType === CustomFieldType.TEXTAREA
                          ? 'text'
                          : 'single',
                    custom: { translateLabel: field.shouldTranslate },
                    ...(definition.isMandatory && {
                        isRequired: (form: ContactForm) => isMandatoryFor(definition, form.relationshipType),
                    }),
                };
            })
        ),
    ];
}

/** The fields the edit form would show for any of the contacts. Every form has the kept contact's party type. */
export function visibleMergeFields(registry: MergeField[], forms: ContactForm[], env: MergeFieldEnv): MergeField[] {
    return registry.filter(
        (field) =>
            (!field.partyType || field.partyType === forms[0].partyType) &&
            (!field.isShown || forms.some((form) => field.isShown?.(form, env)))
    );
}

export function fieldValue(form: ContactForm, key: string): unknown {
    return (form as unknown as Record<string, unknown>)[key];
}

/** A field's label: a custom field's own, unless it is translated, else the form's label key. */
export function fieldLabel(field: MergeField, env: MergeFieldEnv): string {
    return field.custom && !field.custom.translateLabel ? field.labelKey : t(field.labelKey, env.labelValues);
}

/**
 * What converting a contact carries over, by the type it becomes: each field and the field of the other type it
 * takes its value from, as `PartyWriteRepository`'s two conversion upserts map the columns. A person's last name
 * starts empty, and the relations a conversion deletes start empty too.
 */
export const CONVERSION_MAPPING: Record<PartyType, [target: keyof ContactForm, source: keyof ContactForm][]> = {
    [PartyType.PERSON]: [
        ['firstName', 'officialName'],
        ['personalCode', 'regCode'],
        ['homeAddress', 'registrationAddress'],
        ['homeLocalAddress', 'registrationLocalAddress'],
    ],
    [PartyType.COMPANY]: [
        ['officialName', 'name'],
        ['regCode', 'personalCode'],
        ['registrationAddress', 'homeAddress'],
        ['registrationLocalAddress', 'homeLocalAddress'],
    ],
};

const otherType = (partyType: PartyType) => (partyType === PartyType.PERSON ? PartyType.COMPANY : PartyType.PERSON);

/** A contact's form as it reads once converted to `partyType`; the form itself when it already has that type. */
export function convertContactForm(form: ContactForm, partyType: PartyType): ContactForm {
    if (form.partyType === partyType) {
        return form;
    }
    const converted: Record<string, unknown> = {
        ...form,
        partyType,
        employees: null,
        employedBy: null,
        primaryContact: null,
        primaryContactForCompanies: null,
    };
    for (const [target, source] of CONVERSION_MAPPING[partyType]) {
        // Asterisks in place of an obfuscated personal code are no registration number to carry over.
        if (source === 'personalCode' && isPersonalCodeMask(form.personalCode)) continue;
        converted[target] = fieldValue(form, source);
    }
    if (partyType === PartyType.PERSON) {
        converted.lastName = '';
    }
    return converted as unknown as ContactForm;
}

/** The label key of the field a converted contact's value of `key` comes from, or undefined when it is not remapped. */
export function mappedFromLabelKey(key: string, partyType: PartyType): string | undefined {
    const source = CONVERSION_MAPPING[partyType].find(([target]) => target === key)?.[1];
    const fromType = otherType(partyType);
    return FORM_FIELDS.find((field) => field.key === source && (field.partyType ?? fromType) === fromType)?.labelKey;
}

function isAddress(value: object): value is AddressDto {
    return 'address1' in value;
}

/** A list item's identity: an option's code, else an entity's id. */
function itemIdentity(item: unknown): string {
    if (item && typeof item === 'object') {
        const { value, id } = item as { value?: unknown; id?: unknown };
        return String(value ?? id ?? '');
    }
    return String(item ?? '').trim();
}

/**
 * A comparable form of a field's value: '' when it is empty (blank text, null, an empty list or an all-blank
 * address), and otherwise equal exactly when the values are. Lists compare as sets, addresses by their text,
 * text trimmed, and `false` is a value.
 */
export function valueIdentity(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (Array.isArray(value)) {
        return [...new Set(value.map(itemIdentity))].sort().join('\n');
    }
    if (typeof value === 'object' && isAddress(value)) {
        const text = formatFullAddress(value);
        return text === '-' ? '' : text;
    }
    return itemIdentity(value);
}

/**
 * Whether the merged contact keeps its own personal code whatever is chosen: another contact's reads as the asterisks
 * of an obfuscated code, which the backend would not write in its place.
 */
function keepsOwnPersonalCode(field: MergeField, forms: ContactForm[]): boolean {
    return field.key === 'personalCode' && forms.slice(1).some((form) => isPersonalCodeMask(form.personalCode));
}

/**
 * The fields the contacts fill in differently, and how many fields only one of them fills in. A personal code the
 * merged contact keeps (`keepsOwnPersonalCode`) is neither.
 */
export function diffMergeForms(
    forms: ContactForm[],
    registry: MergeField[]
): { differing: MergeField[]; filledOnOneSide: number } {
    const differing: MergeField[] = [];
    let filledOnOneSide = 0;
    for (const field of registry) {
        if (keepsOwnPersonalCode(field, forms)) continue;
        const filled = forms.map((form) => valueIdentity(fieldValue(form, field.key))).filter(Boolean);
        if (new Set(filled).size > 1) {
            differing.push(field);
        } else if (filled.length === 1) {
            filledOnOneSide++;
        }
    }
    return { differing, filledOnOneSide };
}

/**
 * Free texts combined the way the backend combines compliance notes: in the order given, the kept contact's
 * first, blank ones skipped, one already included (compared trimmed) skipped, the rest joined by a blank line.
 * `duplicateOf` gives, for each text, the index of the text it repeats.
 */
export function combineTexts(texts: unknown[]): { parts: string[]; duplicateOf: (number | undefined)[] } {
    const parts: string[] = [];
    const firstIndex = new Map<string, number>();
    const duplicateOf = texts.map((text, index) => {
        const trimmed = typeof text === 'string' ? text.trim() : '';
        if (!trimmed) {
            return undefined;
        }
        const first = firstIndex.get(trimmed);
        if (first === undefined) {
            firstIndex.set(trimmed, index);
            parts.push(text as string);
        }
        return first;
    });
    return { parts, duplicateOf };
}

/** The ticked contacts' free texts combined (see `combineTexts`); every contact is ticked by default. */
export function combineTickedTexts(
    texts: unknown[],
    contactIds: number[],
    choice: MergeChoice | undefined
): ReturnType<typeof combineTexts> {
    const ticked = Array.isArray(choice) ? choice : contactIds;
    return combineTexts(texts.map((text, index) => (ticked.includes(contactIds[index]) ? text : undefined)));
}

/** A list item that carries notes (a related keyword) keeps the notes of all its copies, combined as free texts are. */
function withCombinedNotes(item: unknown, copies: unknown[]): unknown {
    if (!item || typeof item !== 'object' || !('notes' in item)) {
        return item;
    }
    const notes = combineTexts(copies.map((copy) => (copy as { notes?: unknown }).notes)).parts.join('\n\n');
    return notes ? { ...item, notes } : item;
}

/**
 * A registry field's value on the merged contact, from the forms in column order, the kept contact's first. A
 * single-value field takes the chosen contact's value, by default the kept contact's or, when it has none, the
 * first one filled in. A free-text field combines the ticked contacts' texts, by default all of them. A multi-value
 * field keeps the values of the ticked contacts, by default all of them, each once with its copies' notes combined.
 * A field nobody fills in keeps the kept contact's value, and so does a personal code another contact holds as
 * asterisks.
 */
export function mergedValue(
    field: MergeField,
    forms: ContactForm[],
    contactIds: number[],
    choice: MergeChoice | undefined
): unknown {
    const values = forms.map((form) => fieldValue(form, field.key));
    if (keepsOwnPersonalCode(field, forms)) {
        return values[0];
    }
    const filled = values
        .map((value, index) => ({ value, id: contactIds[index] }))
        .filter(({ value }) => valueIdentity(value));
    if (filled.length === 0) {
        return values[0];
    }
    if (field.kind === 'multi') {
        const ticked = Array.isArray(choice) ? choice : contactIds;
        const items = filled.filter(({ id }) => ticked.includes(id)).flatMap(({ value }) => value as unknown[]);
        const copies = new Map<string, unknown[]>();
        items.forEach((item) => {
            const identity = itemIdentity(item);
            copies.set(identity, [...(copies.get(identity) ?? []), item]);
        });
        return [...copies.values()].map((group) => withCombinedNotes(group[0], group));
    }
    if (field.kind === 'text') {
        return combineTickedTexts(values, contactIds, choice).parts.join('\n\n');
    }
    return (filled.find(({ id }) => id === choice) ?? filled[0]).value;
}

/**
 * The kept contact's form with the step 2 choices applied to every registry field (see `mergedValue`), and the
 * defaults to the kept type's fields step 2 does not offer: the ones the form hides for every contact (behind a
 * flag, e-invoicing or its own rule) and the unrendered ones. Lists and references then drop every merged contact,
 * so the kept contact never names itself. The compliance fields stay the kept contact's: the backend combines
 * them itself.
 */
export function buildMergedForm(
    forms: ContactForm[],
    contactIds: number[],
    choices: MergeChoices,
    registry: MergeField[]
): ContactForm {
    const offered = new Set(registry.map(({ key }) => key));
    const notOffered = [...FORM_FIELDS, ...UNRENDERED_FIELDS].filter(
        (field) => !offered.has(field.key) && (!field.partyType || field.partyType === forms[0].partyType)
    );
    const merged: Record<string, unknown> = { ...forms[0] };
    for (const field of [...registry, ...notOffered]) {
        merged[field.key] = mergedValue(field, forms, contactIds, choices[field.key]);
    }

    const form = merged as unknown as ContactForm;
    const isOther = (party: { id: number }) => !contactIds.includes(party.id);
    const otherOrNull = <T extends { id: number }>(party: T | null) => (party && isOther(party) ? party : null);
    return {
        ...form,
        employees: form.employees?.filter(isOther) ?? null,
        employedBy: form.employedBy?.filter(isOther) ?? null,
        primaryContactForCompanies: form.primaryContactForCompanies?.filter(isOther) ?? null,
        secondaryRecipientParties: form.secondaryRecipientParties.filter(isOther),
        payerParty: otherOrNull(form.payerParty),
        primaryRecipientParty: otherOrNull(form.primaryRecipientParty),
        primaryContact: otherOrNull(form.primaryContact),
    };
}

/**
 * The fields the contact form requires on the merged contact the choices make (see `MergeField.isRequired`), and the
 * choices with each of them that keeps no value back on its default: unticking stops at a required field's last box,
 * but choosing a relationship type can make a field required after its boxes were all unticked.
 */
export function applyRequiredFields(
    forms: ContactForm[],
    contactIds: number[],
    choices: MergeChoices,
    registry: MergeField[],
    env: MergeFieldEnv
): { required: Set<string>; choices: MergeChoices } {
    const merged = buildMergedForm(forms, contactIds, choices, registry);
    const required = registry.filter((field) => field.isRequired?.(merged, env));
    const emptied = required.filter(
        ({ key }) => Array.isArray(choices[key]) && !valueIdentity(fieldValue(merged, key))
    );
    return {
        required: new Set(required.map(({ key }) => key)),
        choices: emptied.length
            ? Object.fromEntries(Object.entries(choices).filter(([key]) => !emptied.some((field) => field.key === key)))
            : choices,
    };
}

/**
 * The fields the edit form shows for `form` and would refuse to save it without, left empty. The VAT number is among
 * them only while the card's own-country tick is off: with it on, whether it is required depends on the firm's own
 * country as well.
 */
export function missingRequiredFields(form: ContactForm, registry: MergeField[], env: MergeFieldEnv): MergeField[] {
    return visibleMergeFields(registry, [form], env).filter(
        (field) => field.isRequired?.(form, env) && !valueIdentity(fieldValue(form, field.key))
    );
}

/**
 * Refuses a relationship-type change while a field the new type requires, possibly on another card, is empty. A save
 * that keeps the type goes through: a record already missing such a field stays editable.
 */
export function refuseTypeChangeMissingRequired(
    edited: ContactForm,
    latest: ContactForm,
    registry: MergeField[],
    env: MergeFieldEnv
): void {
    if (edited.relationshipType === latest.relationshipType) return;
    const missing = missingRequiredFields(edited, registry, env);
    if (missing.length > 0) {
        throw new SectionSaveRefused(
            t('contacts.about.fill-required-first', {
                fields: missing.map((field) => fieldLabel(field, env)).join(', '),
            })
        );
    }
}

// The classifiers `contactDtoToForm` resolves codes against, and the relationship type step 2 labels.
const CLASSIFIER_CODES = [
    'RELATIONSHIP_TYPE',
    'PERSON_TITLE',
    'LANGUAGE',
    'BUSINESS_AREA_TYPE',
    'COUNTRY',
    'SEGMENT_TYPE',
    'SERVICE_LEVEL_TYPE',
    'KEYWORD_TYPE',
    'COMPLIANCE_VERIFICATION_STATUS_TYPE',
    'RISK_PROFILE_TYPE',
    'MAILING_PREFERENCE_TYPE',
    'MARKETING_AREA_TYPE',
    'AREA_OF_RESPONSIBILITY_TYPE',
    'COOPERATION_AREA_TYPE',
    'RISK_FACTOR_TYPE_CODE',
];

const NO_BANK_ACCOUNTS: CompanyBankAccountsGroupedDto[] = [];

export type MergeForms = {
    /** The contacts as the edit form loads them, in the order given, each converted to the kept contact's type. */
    forms: ContactForm[];
    /** The fields the edit form shows for any of the contacts. */
    registry: MergeField[];
    env: MergeFieldEnv;
    classifiers: Record<string, ClassifierData>;
    /** The kept contact's custom-field mapping, for `contactFormToRequest`. */
    mapCustomFields: (formData: Record<string, unknown>) => CustomFieldDto[];
    isEinvoicingEnabled: boolean;
};

/**
 * The contacts, the kept one first, as the edit form loads them, and the fields to merge; null while those inputs
 * load. Every contact is read against the kept contact's custom-field definitions, so a converted contact's
 * custom fields, defined for the other type, come out empty.
 */
export function useMergeForms(contacts: ContactDto[]): MergeForms | null {
    const { user } = useAuth();
    const {
        classifiers,
        allClassifiers,
        isLoading: isLoadingClassifiers,
    } = useClassifiers({ codes: CLASSIFIER_CODES });
    const bankAccounts = useGetActiveBankAccountsQuery();
    const einvoicing = useIsEinvoicingEnabled();
    const [kept] = contacts;
    const entityType = kept.partyType === PartyType.COMPANY ? EntityType.PARTY_COMPANY : EntityType.PARTY_PERSON;
    const keptCustomFields = useCustomFields({ entityType, fields: kept.customFields as CustomField[] });

    const enabledFeatures = user?.enabledFeatures;
    const enabledSettings = user?.enabledSettings;
    const requiredFields = user?.requiredFields;
    const administratorFieldName = useAdministratorField().name;
    const isLoading =
        isLoadingClassifiers || bankAccounts.isLoading || einvoicing.isLoading || keptCustomFields.isLoading;
    const bankAccountsGrouped = bankAccounts.data ?? NO_BANK_ACCOUNTS;

    return useMemo(() => {
        if (isLoading) {
            return null;
        }
        const definitions = keptCustomFields.customFieldsEmpty.filter(
            (definition) => definition.fieldType !== CustomFieldType.HEADER
        );
        const forms = contacts.map((contact, index) =>
            convertContactForm(
                contactDtoToForm(contact, {
                    classifiers,
                    bankAccountsGrouped,
                    customFieldValues:
                        index === 0
                            ? keptCustomFields.initialCustomFieldsObject
                            : customFieldFormValues(
                                  definitions.map(
                                      (definition) =>
                                          (contact.customFields as CustomField[] | undefined)?.find(
                                              (field) => field.id === definition.id
                                          ) ?? definition
                                  ),
                                  allClassifiers
                              ),
                }),
                kept.partyType
            )
        );
        const env = mergeFieldEnv(
            enabledFeatures,
            enabledSettings,
            einvoicing.data,
            requiredFields,
            administratorFieldName
        );
        return {
            forms,
            registry: visibleMergeFields(
                mergeFieldRegistry(keptCustomFields.customFields, keptCustomFields.customFieldsEmpty),
                forms,
                env
            ),
            env,
            classifiers,
            mapCustomFields: keptCustomFields.mapCustomFieldsToDto,
            isEinvoicingEnabled: einvoicing.data,
        };
    }, [
        isLoading,
        contacts,
        kept.partyType,
        classifiers,
        allClassifiers,
        bankAccountsGrouped,
        enabledFeatures,
        enabledSettings,
        requiredFields,
        administratorFieldName,
        einvoicing.data,
        keptCustomFields.initialCustomFieldsObject,
        keptCustomFields.customFields,
        keptCustomFields.customFieldsEmpty,
        keptCustomFields.mapCustomFieldsToDto,
    ]);
}
